import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  PaginationParams,
  PaginatedResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ConfirmEmailRequest,
  ResendConfirmationRequest
} from '../types/api';
import { API_CONFIG } from '../config/api.config';
import { applyDeviceTokenHeader } from '../utils/deviceTokenStorage';
import {
  CreateUserRequest,
  UpdateUserRequest,
  UserResponse,
  CreateClientRequest,
  UpdateClientRequest,
  ClientResponse,
  CreateContractRequest,
  UpdateContractRequest,
  ContractResponse,
  CreatePlanRequest,
  UpdatePlanRequest,
  PlanResponse,
  CreateActivityRequest,
  UpdateActivityRequest,
  ActivityResponse,
  CreateRoleRequest,
  UpdateRoleRequest,
  RoleResponse
} from '../types/dtos';

// Classe principal do serviço de API
class ApiService {
  private api: AxiosInstance;
  private isRefreshing = false;
  private refreshPromise: Promise<any> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_CONFIG.baseURL,
      timeout: API_CONFIG.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });
    
    // Teste direto da URL

    // Interceptor para adicionar token de autenticação
    this.api.interceptors.request.use(
      (config) => {
        
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor para tratar erros de resposta com renovação automática de token
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 403 &&
            error.response?.data?.code === 'ACCESS_RESTRICTED') {
          window.location.href = '/access-restricted';
          return Promise.reject(error);
        }

        const originalRequest = error.config;

        // Só tenta renovar token para erros 401 e se não é uma requisição de refresh
        if (error.response?.status === 401 &&
            !originalRequest._retry &&
            !originalRequest.url?.includes('Auth/refresh-token') &&
            !originalRequest.url?.includes('Auth/login')) {

          // Se já está renovando, aguarda a renovação em andamento
          if (this.isRefreshing) {
            if (this.refreshPromise) {
              await this.refreshPromise;
              // Tenta novamente a requisição original
              return this.api(originalRequest);
            }
          }

          // Marca que está renovando
          originalRequest._retry = true;
          this.isRefreshing = true;

          // Tenta renovar o token
          try {
            this.refreshPromise = this.refreshToken();
            const refreshResponse = await this.refreshPromise;

            // Atualiza o token no header da requisição original
            originalRequest.headers.Authorization = `Bearer ${refreshResponse.accessToken}`;

            // Tenta novamente a requisição original
            this.isRefreshing = false;
            this.refreshPromise = null;

            return this.api(originalRequest);
          } catch (refreshError) {
            // Falha na renovação - faz logout  
            this.isRefreshing = false;
            this.refreshPromise = null;

            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            window.location.href = '/login';

            return Promise.reject(refreshError);
          }
        }

        // Para outros erros (não 401) ou se já tentou renovar, rejeita normalmente
        return Promise.reject(error);
      }
    );
  }

  // Método auxiliar para extrair dados da resposta
  private extractData<T>(response: AxiosResponse<ApiResponse<T>>): T {
    if (!response.data.success) {
      throw new Error(response.data.message || 'Erro na requisição');
    }
    return response.data.data!;
  }

  // Métodos públicos para gerenciar headers de autorização
  public setAuthToken(token: string): void {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  public removeAuthToken(): void {
    delete this.api.defaults.headers.common['Authorization'];
  }

  // AUTENTICAÇÃO
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.api.post<ApiResponse<LoginResponse>>('Auth/login', credentials, {
      headers: applyDeviceTokenHeader(undefined) as Record<string, string>,
    });
    return this.extractData(response);
  }

  async register(userData: RegisterRequest): Promise<ApiResponse<void>> {
    const response = await this.api.post<ApiResponse<void>>('Auth/register', userData);
    return response.data;
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<ApiResponse<void>> {
    const response = await this.api.post<ApiResponse<void>>('Auth/forgot-password', data);
    return response.data;
  }

  async resetPassword(data: ResetPasswordRequest): Promise<ApiResponse<void>> {
    const response = await this.api.post<ApiResponse<void>>('Auth/reset-password', data);
    return response.data;
  }

  async confirmEmail(data: ConfirmEmailRequest): Promise<ApiResponse<void>> {
    const response = await this.api.post<ApiResponse<void>>('Auth/confirm-email', data);
    return response.data;
  }

  async resendConfirmation(data: ResendConfirmationRequest): Promise<ApiResponse<void>> {
    const response = await this.api.post<ApiResponse<void>>('Auth/resend-confirmation', data);
    return response.data;
  }

  async refreshToken(): Promise<LoginResponse> {
    const response = await this.api.post<ApiResponse<LoginResponse>>('Auth/refresh-token', null, {
      headers: applyDeviceTokenHeader(undefined) as Record<string, string>,
    });
    return this.extractData(response);
  }

  // USUÁRIOS
  async getUsers(params?: PaginationParams): Promise<PaginatedResponse<UserResponse>> {

    const response = await this.api.get('users', { params });

    // Extrair dados da estrutura ApiResponse
    let data;
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro ao consultar usuários');
      }
      data = response.data.data;
    } else {
      data = response.data;
    }

    // Tratar dados paginados ou lista simples
    if (Array.isArray(data)) {
      return {
        items: data,
        totalCount: data.length,
        pageNumber: params?.page || 1,
        pageSize: params?.pageSize || 10,
        totalPages: Math.ceil(data.length / (params?.pageSize || 10)),
        hasNextPage: false,
        hasPreviousPage: false
      };
    }

    return {
      items: data?.items || [],
      totalCount: data?.totalCount || 0,
      pageNumber: data?.pageNumber || 1,
      pageSize: data?.pageSize || 10,
      totalPages: data?.totalPages || 1,
      hasNextPage: data?.hasNextPage || false,
      hasPreviousPage: data?.hasPreviousPage || false
    };
  }

  async getUserById(id: string): Promise<UserResponse> {
    const response = await this.api.get<ApiResponse<UserResponse>>(`/users/${id}`);
    return this.extractData(response);
  }

  async createUser(userData: CreateUserRequest): Promise<UserResponse> {
    const response = await this.api.post<ApiResponse<UserResponse>>('/users', userData);
    return this.extractData(response);
  }

  async updateUser(id: string, userData: UpdateUserRequest): Promise<UserResponse> {
    const response = await this.api.put<ApiResponse<UserResponse>>(`/users/${id}`, userData);
    return this.extractData(response);
  }

  async deleteUser(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete<ApiResponse<void>>(`/users/${id}`);
    return response.data;
  }

  async renewUserToken(id: string): Promise<ApiResponse<string>> {
    const response = await this.api.post<ApiResponse<string>>(`/users/${id}/renew-token`);
    return response.data;
  }

  async activateUser(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.post<ApiResponse<void>>(`/users/${id}/activate`);
    return response.data;
  }

  async deactivateUser(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.post<ApiResponse<void>>(`/users/${id}/deactivate`);
    return response.data;
  }

  async resetUserPassword(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.post<ApiResponse<void>>(`/users/${id}/reset-password`);
    return response.data;
  }

  // CLIENTES
  async getClients(params?: PaginationParams): Promise<PaginatedResponse<ClientResponse>> {
    const response = await this.api.get<ApiResponse<PaginatedResponse<ClientResponse>>>('/clients', { params });
    return this.extractData(response);
  }

  async getClientById(id: string): Promise<ClientResponse> {
    const response = await this.api.get<ApiResponse<ClientResponse>>(`/clients/${id}`);
    return this.extractData(response);
  }

  async createClient(clientData: CreateClientRequest): Promise<ClientResponse> {
    const response = await this.api.post<ApiResponse<ClientResponse>>('/clients', clientData);
    return this.extractData(response);
  }

  async updateClient(id: string, clientData: UpdateClientRequest): Promise<ClientResponse> {
    const response = await this.api.put<ApiResponse<ClientResponse>>(`/clients/${id}`, clientData);
    return this.extractData(response);
  }

  async deleteClient(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete<ApiResponse<void>>(`/clients/${id}`);
    return response.data;
  }

  // CONTRATOS
  async getContracts(params?: PaginationParams): Promise<PaginatedResponse<ContractResponse>> {
    const response = await this.api.get<ApiResponse<PaginatedResponse<ContractResponse>>>('/contracts', { params });
    return this.extractData(response);
  }

  async getContractById(id: number): Promise<ContractResponse> {
    const response = await this.api.get<ApiResponse<ContractResponse>>(`/contracts/${id}`);
    return this.extractData(response);
  }

  async createContract(contractData: CreateContractRequest): Promise<ContractResponse> {
    const response = await this.api.post<ApiResponse<ContractResponse>>('/contracts', contractData);
    return this.extractData(response);
  }

  async updateContract(id: number, contractData: UpdateContractRequest): Promise<ContractResponse> {
    const response = await this.api.put<ApiResponse<ContractResponse>>(`/contracts/${id}`, contractData);
    return this.extractData(response);
  }

  async deleteContract(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.delete<ApiResponse<void>>(`/contracts/${id}`);
    return response.data;
  }

  // PLANOS
  async getPlans(params?: PaginationParams): Promise<PaginatedResponse<PlanResponse>> {
    const response = await this.api.get<ApiResponse<PaginatedResponse<PlanResponse>>>('/plans', { params });
    return this.extractData(response);
  }

  async getPlanById(id: number): Promise<PlanResponse> {
    const response = await this.api.get<ApiResponse<PlanResponse>>(`/plans/${id}`);
    return this.extractData(response);
  }

  async createPlan(planData: CreatePlanRequest): Promise<PlanResponse> {
    const response = await this.api.post<ApiResponse<PlanResponse>>('/plans', planData);
    return this.extractData(response);
  }

  async updatePlan(id: number, planData: UpdatePlanRequest): Promise<PlanResponse> {
    const response = await this.api.put<ApiResponse<PlanResponse>>(`/plans/${id}`, planData);
    return this.extractData(response);
  }

  async deletePlan(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.delete<ApiResponse<void>>(`/plans/${id}`);
    return response.data;
  }

  // ATIVIDADES
  async getActivities(params?: PaginationParams): Promise<PaginatedResponse<ActivityResponse>> {
    const response = await this.api.get<ApiResponse<PaginatedResponse<ActivityResponse>>>('/activities', { params });
    return this.extractData(response);
  }

  async getActivityById(id: number): Promise<ActivityResponse> {
    const response = await this.api.get<ApiResponse<ActivityResponse>>(`/activities/${id}`);
    return this.extractData(response);
  }

  async createActivity(activityData: CreateActivityRequest): Promise<ActivityResponse> {
    const response = await this.api.post<ApiResponse<ActivityResponse>>('/activities', activityData);
    return this.extractData(response);
  }

  async updateActivity(id: number, activityData: UpdateActivityRequest): Promise<ActivityResponse> {
    const response = await this.api.put<ApiResponse<ActivityResponse>>(`/activities/${id}`, activityData);
    return this.extractData(response);
  }

  async deleteActivity(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.delete<ApiResponse<void>>(`/activities/${id}`);
    return response.data;
  }

  // PAPEIS/ROLES
  async getRoles(params?: PaginationParams): Promise<PaginatedResponse<RoleResponse>> {
    const response = await this.api.get<ApiResponse<PaginatedResponse<RoleResponse>>>('/roles', { params });
    return this.extractData(response);
  }

  // Método otimizado para dropdowns de roles
  async getRolesDropdown(): Promise<any[]> {
    const response = await this.api.get<any[]>('/roles/dropdown');
    return response.data;
  }

  async getRoleById(id: number): Promise<RoleResponse> {
    const response = await this.api.get<ApiResponse<RoleResponse>>(`/roles/${id}`);
    return this.extractData(response);
  }

  async createRole(roleData: CreateRoleRequest): Promise<RoleResponse> {
    const response = await this.api.post<ApiResponse<RoleResponse>>('/roles', roleData);
    return this.extractData(response);
  }

  async updateRole(id: number, roleData: UpdateRoleRequest): Promise<RoleResponse> {
    const response = await this.api.put<ApiResponse<RoleResponse>>(`/roles/${id}`, roleData);
    return this.extractData(response);
  }

  async deleteRole(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.delete<ApiResponse<void>>(`/roles/${id}`);
    return response.data;
  }

  async getPosVendaEmails(params: Record<string, string | number | undefined>): Promise<any> {
    const response = await this.api.get('/pos-venda/emails', { params });
    return response.data;
  }

  async getPosVendaEmail(id: string): Promise<any> {
    const response = await this.api.get(`/pos-venda/emails/${id}`);
    return response.data;
  }

  async resendPosVendaEmail(id: string, toEmail?: string): Promise<void> {
    await this.api.post(`/pos-venda/emails/${id}/reenviar`, { toEmail: toEmail || null });
  }
}

// Exporta uma instância singleton
export const apiService = new ApiService();
export default apiService;
